import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMissatgeError } from '@/api/errors';
import { esConflicteVersio } from '@/api/etag';
import { Icona, type NomIcona } from '@/components/Icona';
import { tempsDes } from './utils';

/** «fa 6 min», «fa 2 dies»… */
export function FaTemps({ iso }: { iso?: string | null }) {
  const { t } = useTranslation('administracio');
  const r = tempsDes(iso);
  if (!r) return <>{'—'}</>;
  return <>{t(`comu.fa.${r.unitat}`, { count: r.n })}</>;
}

/**
 * Error en desar. Un 412 (algú altre ha modificat les dades des que s'han carregat) es mostra
 * amb el missatge traduït de conflicte de versió i, si es pot, un botó per tornar a carregar.
 */
export function ErrorDesa({ error, onRecarrega }: { error: unknown; onRecarrega?: () => void }) {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  if (!error) return null;
  const conflicte = esConflicteVersio(error);
  return (
    <div className="avis-banda" role="alert">
      <Icona nom="exclamationmark-triangle" />
      <span style={{ flex: 1 }}>
        {conflicte
          ? t('error.etag.no.coincideix', { ns: 'errors', keySeparator: false })
          : missatgeError(error)}
      </span>
      {conflicte && onRecarrega ? (
        <button type="button" className="btn btn--xs" onClick={onRecarrega}>
          <Icona nom="arrow-clockwise" />
          {t('comu.recarrega')}
        </button>
      ) : null}
    </div>
  );
}

/** Camp de cerca amb lupa (mockups `.search`). */
export function Cerca({
  valor,
  onCanvia,
  placeholder,
  className,
}: {
  valor: string;
  onCanvia: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <label className={`search${className ? ` ${className}` : ''}`}>
      <Icona nom="magnifier" />
      <input
        type="search"
        value={valor}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onCanvia(e.target.value)}
      />
    </label>
  );
}

/** Capçalera de panell amb icona opcional i text secundari a la dreta. */
export function CapPanell({
  titol,
  icona,
  colorIcona,
  extra,
  children,
}: {
  titol: string;
  icona?: NomIcona;
  colorIcona?: string;
  extra?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="panel__head">
      {icona ? <Icona nom={icona} className={colorIcona} /> : null}
      <h3>{titol}</h3>
      <div className="spacer" />
      {extra ? <span className="t-caption-2 dim">{extra}</span> : null}
      {children}
    </div>
  );
}
